import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Flex,
  FlexProps,
} from "@chakra-ui/react";
import { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";
import { useIsSmallBreakpoint, useMobileBreakpoint } from "../../utils/useBreakpoints";
import { ArrowLeftIcon } from "../icons/ArrowLeftIcon";
import { ArrowRightIcon } from "../icons/ArrowRightIcon";
import { isArray } from "lodash-es";

interface ButtonCarouselProps {
  big?: boolean;
  children: React.ReactNode;
  wrapperProps?: FlexProps;
  containerProps?: FlexProps;
  includeButtons?: boolean;
  includeSpacer?: boolean;
  width?: BoxProps["width"];
  showScrollbar?: boolean;
  onlyFade?: boolean;
  dontFadeButtons?: boolean;
  activeSlideIndex?: number;
  onEmblaSelect?: (selectedIndex: number) => void;
  alwaysShowButtons?: boolean;
  buttonBg?: string;
}

export function ButtonCarousel({
  big = false,
  children,
  wrapperProps,
  containerProps,
  includeButtons = true,
  includeSpacer = false,
  width = "calc(100% - 190px)",
  showScrollbar = false,
  onlyFade = false,
  dontFadeButtons = false,
  activeSlideIndex,
  onEmblaSelect,

  alwaysShowButtons = false,
  buttonBg,

  // Native embla options
  align = "start",
  slidesToScroll = "auto",
}: ButtonCarouselProps & EmblaOptionsType) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll,
    align,
    skipSnaps: true,
    dragFree: true,
  });
  const isSmall = useIsSmallBreakpoint();

  const { prevBtnDisabled, nextBtnDisabled } = usePrevNextButtons(emblaApi);

  const handlePrevButtonClick = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
    }
  }, [emblaApi]);

  const handleNextButtonClick = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
    }
  }, [emblaApi]);

  const mask = `
      linear-gradient(
        to right, 
        ${prevBtnDisabled ? "black" : "transparent"} 0%, 
        black 13%,
        black 77%,
        ${nextBtnDisabled ? "black" : "transparent"} 100% 
      )
    `;

  useEffect(() => {
    if (emblaApi && activeSlideIndex !== undefined) {
      emblaApi.scrollTo(activeSlideIndex);
    }
  }, [emblaApi, activeSlideIndex]);

  useEffect(() => {
    if (!emblaApi || !onEmblaSelect) {
      return;
    }

    const onSelect = () => {
      onEmblaSelect(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onEmblaSelect]);

  if (!isArray(children) || children.length === 0) return null;
  return (
    <Box
      alignItems="center"
      position="relative"
      width={width}
      aria-roledescription="carousel"
      {...wrapperProps}
    >
      <Box
        ref={showScrollbar ? undefined : emblaRef}
        overflow="hidden"
        sx={
          (prevBtnDisabled && nextBtnDisabled) || !includeButtons
            ? {}
            : { mask }
        }
      >
        <Flex
          overflow={showScrollbar ? "auto" : undefined}
          {...containerProps}
          // This shouldn't be overridden
          className={showScrollbar ? "thin-scrollbar" : undefined}
          sx={
            showScrollbar
              ? undefined
              : {
                  backfaceVisibility: "hidden",
                  touchAction: "pan-y pinch-zoom",
                }
          }
        >
          {children}
          {includeSpacer && <Box minWidth={4} />}
        </Flex>
      </Box>
      {onlyFade || includeButtons ? (
        <>
          <PrevButton
            big={big}
            onClick={handlePrevButtonClick}
            isDisabled={(!alwaysShowButtons && isSmall) || prevBtnDisabled}
            showButtons={!onlyFade}
            dontFadeButtons={dontFadeButtons}
            customBg={buttonBg}
          />
          <NextButton
            big={big}
            onClick={handleNextButtonClick}
            isDisabled={(!alwaysShowButtons && isSmall) || nextBtnDisabled}
            showButtons={!onlyFade}
            dontFadeButtons={dontFadeButtons}
            customBg={buttonBg}
          />
        </>
      ) : null}
    </Box>
  );
}

export function PrevButton(
  props: ButtonProps & {
    big?: boolean;
    showButtons?: boolean;
    alwaysShowButtons?: boolean;
    dontFadeButtons?: boolean;
    customBg?: string;
  },
) {
  const isMobile = useMobileBreakpoint();
  const { big, showButtons, dontFadeButtons, customBg, ...rest } = props;
  const defaultBg = big
    ? "transparent"
    : "linear-gradient(90deg, var(--chakra-colors-charcoal-charcoal) 0%, var(--chakra-colors-charcoal-charcoal) 72.5%, rgba(20, 21, 23, 0.00) 100%)";
  return (
    <Flex
      position="absolute"
      role="group"
      left={big ? (isMobile ? "16px" : "38px") : 0}
      top={0}
      alignItems="center"
      bg={customBg ?? defaultBg}
      opacity={props.isDisabled ? 0 : 1}
      pointerEvents={props.isDisabled ? "none" : "all"}
      height="100%"
    >
      {showButtons && (
        <Button
          transition="all .2s linear"
          variant="iconOnly"
          p={0}
          color={!dontFadeButtons && big ? "transparent" : "dune.700"}
          _groupHover={{ color: "dune.100" }}
          {...rest}
          aria-label="Previous Button"
        >
          <ArrowLeftIcon w={big ? 10 : 6} h={big ? 10 : 6} />
        </Button>
      )}
    </Flex>
  );
}

export function NextButton(
  props: ButtonProps & {
    big?: boolean;
    showButtons?: boolean;
    dontFadeButtons?: boolean;
    customBg?: string;
  },
) {
  const isMobile = useMobileBreakpoint();
  const { big, showButtons, dontFadeButtons, customBg, ...rest } = props;
  const defaultBg = big
    ? "transparent"
    : "linear-gradient(270deg, var(--chakra-colors-charcoal-charcoal) 0%, var(--chakra-colors-charcoal-charcoal) 72.5%, rgba(20, 21, 23, 0.00) 100%)";
  return (
    <Flex
      position="absolute"
      role="group"
      top={0}
      right={big ? (isMobile ? "16px" : "38px") : 0}
      alignItems="center"
      bg={customBg ?? defaultBg}
      opacity={props.isDisabled ? 0 : 1}
      pointerEvents={props.isDisabled ? "none" : "all"}
      height="100%"
    >
      {showButtons && (
        <Button
          transition="all .2s linear"
          variant="iconOnly"
          color={!dontFadeButtons && big ? "transparent" : "dune.700"}
          _groupHover={{ color: "dune.100" }}
          {...rest}
          aria-label="Next Button"
        >
          <ArrowRightIcon w={big ? 10 : 6} h={big ? 10 : 6} />
        </Button>
      )}
    </Flex>
  );
}

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};
export const usePrevNextButtons = (
  emblaApi?: EmblaCarouselType,
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};
